import { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { usePermissions } from "@/lib/permissions";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDate, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { HideWhenSingleCompany } from "@/components/CompanyField";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const WEEKS = ["4", "8", "13", "26"];

function Tile({ label, value, sub, tone, testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 text-xl font-bold tabular-nums ${tone || "text-foreground"}`}>{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

/** What is expected to come in and go out, week by week, from the cash held today. */
export default function CashForecast() {
  const { companies } = useCompany() || {};
  const { can } = usePermissions();
  const canWrite = can("GENERAL_LEDGER", "create");
  const [companyId, setCompanyId] = useState("");
  const [weeks, setWeeks] = useState("13");
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(new Set());
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const cid = companyId || companies?.[0]?.id;

  const load = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const [{ data: f }, { data: it }] = await Promise.all([
        api.get("/reports/cash-forecast", { params: { company_id: cid, weeks: Number(weeks) } }),
        api.get("/cash-forecast/items", { params: { company_id: cid } }),
      ]);
      setData(f); setItems(it); setOpen(new Set());
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setLoading(false); }
  }, [cid, weeks]);
  useEffect(() => { load(); }, [load]);

  const toggle = (i) => setOpen((s) => { const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n; });
  const close = async (item, status) => {
    try { await api.patch(`/cash-forecast/items/${item.id}`, { status }); toast.success(status === "DONE" ? "Marked as done" : "Cancelled"); load(); } catch (err) { toast.error(getErrorMessage(err)); }
  };
  const remove = async (item) => {
    try { await api.delete(`/cash-forecast/items/${item.id}`); toast.success("Removed"); load(); } catch (err) { toast.error(getErrorMessage(err)); }
  };
  const maxAbs = data ? Math.max(1, ...data.rows.map((r) => Math.abs(Number(r.closing)))) : 1;

  return (
    <div className="space-y-5" data-testid="cash-forecast-page">
      <PageHeader title="Cash Forecast" description="What is expected to come in and go out each week, from the cash held today. Client bills fall due by their credit days, vendor bills by theirs."
        action={canWrite && cid && <Button onClick={() => setAdding(true)} data-testid="forecast-add"><Plus className="h-4 w-4" /> Add expected receipt or payment</Button>} />
      <div className="flex flex-wrap items-end gap-3">
        <HideWhenSingleCompany>
          <div className="space-y-1"><Label className="text-xs">Company</Label>
            <Select value={cid || ""} onValueChange={setCompanyId}><SelectTrigger className="w-56" data-testid="forecast-company"><SelectValue placeholder="Company" /></SelectTrigger>
              <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
        </HideWhenSingleCompany>
        <div className="space-y-1"><Label className="text-xs">Look ahead</Label>
          <Select value={weeks} onValueChange={setWeeks}><SelectTrigger className="w-36" data-testid="forecast-weeks"><SelectValue /></SelectTrigger>
            <SelectContent>{WEEKS.map((w) => <SelectItem key={w} value={w}>{w} weeks</SelectItem>)}</SelectContent></Select></div>
        {loading && <Loader2 className="mb-2 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {!data ? (loading ? null : <EmptyState title="Choose a company" description="The forecast is worked out for one company at a time." testId="forecast-empty" />) : (
        <>
          {data.first_shortfall_week != null && (
            <div className="flex items-start gap-2 rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800" data-testid="forecast-shortfall">
              <AlertTriangle className="mt-0.5 h-4 w-4" />
              <span>Cash runs short from <b>week {data.first_shortfall_week + 1}</b> ({fmtDate(data.rows[data.first_shortfall_week].start)}). The lowest balance is <b>{inr(data.lowest_cash)}</b>.</span>
            </div>)}
          {!data.cash_accounts_set && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="forecast-no-cash-accounts">
              No account is marked as cash and bank in the Chart of Accounts, so the forecast starts from zero.</div>)}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="forecast-tiles">
            <Tile label="Cash today" value={data.opening_cash != null ? inr(data.opening_cash) : "—"} testId="fc-opening" />
            <Tile label={`Cash after ${data.weeks} weeks`} value={inr(data.closing_cash)} tone={Number(data.closing_cash) < 0 ? "text-rose-600" : ""} testId="fc-closing" />
            <Tile label="Lowest cash" value={inr(data.lowest_cash)} sub={`week ${data.lowest_week + 1}`} tone={Number(data.lowest_cash) < 0 ? "text-rose-600" : ""} testId="fc-lowest" />
            <Tile label="Clients overdue" value={inr(data.overdue_receivables)} tone={Number(data.overdue_receivables) > 0 ? "text-amber-600" : ""} testId="fc-overdue-in" />
            <Tile label="Vendors overdue" value={inr(data.overdue_payables)} tone={Number(data.overdue_payables) > 0 ? "text-amber-600" : ""} testId="fc-overdue-out" />
            <Tile label="DSO" value={data.dso != null ? `${data.dso} days` : "—"} sub="of the last 90 days' billing" testId="fc-dso" />
          </div>

          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <Table data-testid="forecast-table">
              <TableHeader><TableRow>
                <TableHead /><TableHead>Week</TableHead><TableHead className="text-right">Opening</TableHead><TableHead className="text-right">Client receipts</TableHead><TableHead className="text-right">Other in</TableHead>
                <TableHead className="text-right">Vendor payments</TableHead><TableHead className="text-right">Other out</TableHead><TableHead className="text-right">Closing</TableHead><TableHead className="w-40" />
              </TableRow></TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <Fragment key={r.index}>
                    <TableRow className="cursor-pointer hover:bg-muted/40" onClick={() => toggle(r.index)} data-testid={`week-${r.index}`}>
                      <TableCell className="w-8">{r.items.length ? (open.has(r.index) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : null}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.index + 1}. {fmtDate(r.start)} – {fmtDate(r.end)}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{inr(r.opening)}</TableCell>
                      <TableCell className="text-right tabular-nums">{Number(r.receipts) ? inr(r.receipts) : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{Number(r.other_in) ? inr(r.other_in) : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{Number(r.payments) ? inr(r.payments) : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{Number(r.other_out) ? inr(r.other_out) : "—"}</TableCell>
                      <TableCell className={`text-right font-semibold tabular-nums ${r.short ? "text-rose-600" : ""}`} data-testid={`week-closing-${r.index}`}>{inr(r.closing)}</TableCell>
                      <TableCell><div className="h-2 rounded bg-muted"><div className={`h-2 rounded ${r.short ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${Math.round((Math.abs(Number(r.closing)) / maxAbs) * 100)}%` }} /></div></TableCell>
                    </TableRow>
                    {open.has(r.index) && r.items.map((it, n) => (
                      <TableRow key={`${r.index}-${n}`} className="bg-muted/20 text-xs" data-testid={`week-${r.index}-item-${n}`}>
                        <TableCell /><TableCell colSpan={2}>{it.direction === "IN" ? "In" : "Out"} · {it.kind === "MANUAL" ? it.ref : `${it.ref} · ${it.party}`}</TableCell>
                        <TableCell colSpan={4} className="text-muted-foreground">due {fmtDate(it.due_date)}{it.overdue ? ` (${it.days_past_due} days overdue)` : ""}</TableCell>
                        <TableCell className="text-right tabular-nums">{it.direction === "IN" ? "+" : "−"}{inr(it.amount)}</TableCell><TableCell />
                      </TableRow>))}
                  </Fragment>))}
              </TableBody>
            </Table>
          </div>
          {(Number(data.later_receipts) > 0 || Number(data.later_payments) > 0) && (
            <p className="text-sm text-muted-foreground" data-testid="forecast-later">Beyond these {data.weeks} weeks: {inr(data.later_receipts)} still to come in from clients and {inr(data.later_payments)} still to go out to vendors.</p>)}

          <div className="rounded-xl border border-border bg-card" data-testid="forecast-items">
            <p className="px-4 pt-3 text-sm font-medium">Expected receipts and payments you entered</p>
            {items.length === 0 ? <p className="px-4 py-4 text-sm text-muted-foreground" data-testid="forecast-items-empty">None. Add a loan instalment, or an advance a client has promised.</p> : (
              <Table><TableBody>
                {items.map((i) => (
                  <TableRow key={i.id} data-testid={`item-${i.description}`}>
                    <TableCell>{i.direction === "IN" ? "Receipt" : "Payment"}</TableCell><TableCell>{i.description}</TableCell><TableCell>{fmtDate(i.expected_date)}</TableCell>
                    <TableCell className="text-right tabular-nums">{inr(i.amount)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">{canWrite && <>
                      <Button size="sm" variant="outline" onClick={() => close(i, "DONE")} data-testid={`item-done-${i.description}`}>Done</Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(i)} aria-label={`Remove ${i.description}`} data-testid={`item-remove-${i.description}`}><Trash2 className="h-3.5 w-3.5" /></Button></>}</TableCell>
                  </TableRow>))}
              </TableBody></Table>)}
          </div>
          <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground" data-testid="forecast-assumptions">{data.assumptions.map((a) => <li key={a}>{a}</li>)}</ul>
        </>)}
      {adding && <AddItem companyId={cid} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
}

function AddItem({ companyId, onClose, onSaved }) {
  const [f, setF] = useState({ direction: "OUT", expected_date: todayISO(), amount: "", description: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try { await api.post("/cash-forecast/items", { ...f, company_id: companyId, amount: Number(f.amount) }); toast.success("Added to the forecast"); onSaved(); }
    catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="forecast-add-dialog">
        <DialogHeader><DialogTitle>Add an expected receipt or payment</DialogTitle><DialogDescription>Something you know is coming that is not on a bill: a loan instalment, an advance a client promised.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Type</Label>
            <Select value={f.direction} onValueChange={(v) => setF((x) => ({ ...x, direction: v }))}><SelectTrigger className="mt-1.5" data-testid="forecast-direction"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="OUT">Payment (money out)</SelectItem><SelectItem value="IN">Receipt (money in)</SelectItem></SelectContent></Select></div>
          <div><Label>Expected on</Label><Input type="date" value={f.expected_date} onChange={set("expected_date")} className="mt-1.5" data-testid="forecast-date" /></div>
          <div><Label>Amount (₹)</Label><Input type="number" min="0" value={f.amount} onChange={set("amount")} className="mt-1.5" data-testid="forecast-amount" /></div>
          <div><Label>What it is</Label><Input value={f.description} maxLength={200} onChange={set("description")} className="mt-1.5" data-testid="forecast-description" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={saving || !(Number(f.amount) > 0) || !f.description.trim()} onClick={save} data-testid="forecast-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
